import React, { useMemo, useState, useEffect } from 'react';
import Icon from '../comps/Icon';
import menuSvc from '../services/menu';
import gallerySvc from '../services/gallery';
import openingHoursSvc from '../services/openingHours';
import categoriesSvc from '../services/categories';
import '../style/css/businesscontent2026.css';

// Build the 7-day hours editor state from the real openingTimes rows. Weekly rows (type 1)
// become each day's services; dayInWeek is 1=Sunday…7=Saturday (date('w')+1 → index = -1).
const HE_DAYS_LIST = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
function buildHoursFromRows(rows) {
  const days = HE_DAYS_LIST.map((day) => ({ day, open: false, services: [] }));
  const ids = [];
  (rows || []).forEach((r) => {
    // Only track type-1 (weekly) IDs — this editor's replace-all save must NOT delete the
    // type 2/3 one-time open/closed EXCEPTIONS created in the Settings hours editor.
    if (Number(r.type) !== 1) return;
    ids.push(r.ID ?? r.id);
    const idx = Number(r.dayInWeek) - 1;
    if (idx < 0 || idx > 6) return;
    days[idx].open = true;
    days[idx].services.push({
      name: r.serviceName || 'סרביס',
      from: String(r.startTime || '').slice(0, 5),
      to: String(r.endTime || '').slice(0, 5),
      seats: r.seats != null ? Number(r.seats) : 60,
      interval: r.intervalMinutes != null ? Number(r.intervalMinutes) : 90,
    });
  });
  return { days, ids };
}

// Group the flat DB menu items into the nested {cat, dishes:[{id,name,desc,price}]} the UI
// uses, preserving the server order (sortOrder, ID). Each dish keeps its DB id.
function groupMenu(items) {
  const order = [];
  const byCat = {};
  (items || []).forEach((it) => {
    const cat = it.category || 'ללא קטגוריה';
    if (!byCat[cat]) { byCat[cat] = []; order.push(cat); }
    byCat[cat].push({ id: it.id, name: it.name, desc: it.description, price: it.price });
  });
  return order.map((cat) => ({ cat, dishes: byCat[cat] }));
}

/*
 * BusinessContent — the BUSINESS CONTENT MANAGER for the HOPPA operator app.
 *
 * Operator intent (verbatim): "לכל עסק צריך להיות קטגוריות משלו, תפריט,
 * גלריית תמונות, שעות פעילות (עם אופציה לסרביסים)".
 *
 * This is the authenticated screen where a restaurant owner edits what customers
 * see on their PUBLIC page (src/pages/public/PublicBusinessPage.js). Four sections
 * switched by a segmented control: קטגוריות · תפריט · גלריה · שעות פעילות+סרביסים.
 *
 * All four tabs are REAL + multi-tenant: categories (categoriesList/categoryAdd/categoryDel),
 * menu (menuList/menuItem*), gallery (galleryList/galleryUpload/Delete/SetCover — server-side
 * re-encode+storage), hours (openingTimes acts, type-1 weekly rows w/ serviceName/seats/interval).
 * NOTE: the Settings BusinessWeekScheduler edits the SAME businessOpeningTimes table — this tab
 * only touches type-1 weekly rows.
 */

/* ------------------------------------------------------------------ *
 *  MOCK DATA — replace with future getBusinessContent act
 * ------------------------------------------------------------------ */



// Gallery — emoji tiles rendered over CSS gradients (NO external URLs), exactly
// like the public page. `cover` marks the hero/cover image.

// A small palette to cycle through when a new gallery tile is added.

// Hebrew weekdays, Sunday-first (RTL renders ראשון on the right).


/* ------------------------------------------------------------------ *
 *  Constants
 * ------------------------------------------------------------------ */

const TABS = [
  { id: 'categories', label: 'קטגוריות', icon: 'tag' },
  { id: 'menu', label: 'תפריט', icon: 'list' },
  { id: 'gallery', label: 'גלריה', icon: 'image' },
  { id: 'hours', label: 'שעות פעילות', icon: 'clock' },
];


/* ------------------------------------------------------------------ *
 *  Component
 * ------------------------------------------------------------------ */

const BusinessContent = () => {
  const [tab, setTab] = useState('categories');

  const [categories, setCategories] = useState([]); // real {id,name} from the DB
  const [catLoading, setCatLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      const cats = await categoriesSvc.listCategories();
      if (alive) { setCategories(cats); setCatLoading(false); }
    })();
    return () => { alive = false; };
  }, []);
  const [menu, setMenu] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);

  // Load the real menu (multi-tenant: the act returns only this business's items).
  useEffect(() => {
    let alive = true;
    (async () => {
      const items = await menuSvc.listMenu();
      if (alive) { setMenu(groupMenu(items)); setMenuLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Re-sync the menu from the server (used to recover after a failed write).
  const refreshMenu = async () => { setMenu(groupMenu(await menuSvc.listMenu())); };
  const [gallery, setGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Load the real gallery (multi-tenant; images served from /media/gallery/<businessID>/).
  useEffect(() => {
    let alive = true;
    (async () => {
      const imgs = await gallerySvc.listGallery();
      if (alive) { setGallery(imgs); setGalleryLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const refreshGallery = async () => { setGallery(await gallerySvc.listGallery()); };
  const [hours, setHours] = useState(() => HE_DAYS_LIST.map((day) => ({ day, open: false, services: [] })));
  const [hoursLoadedIds, setHoursLoadedIds] = useState([]);
  const [hoursSaving, setHoursSaving] = useState(false);

  // Load real opening hours (multi-tenant; same acts as the Settings hours editor).
  useEffect(() => {
    let alive = true;
    (async () => {
      const { days, ids } = buildHoursFromRows(await openingHoursSvc.listHours());
      if (alive) { setHours(days); setHoursLoadedIds(ids); }
    })();
    return () => { alive = false; };
  }, []);

  // Save = replace-all: delete the persisted rows, then re-insert the current schedule as
  // type-1 weekly rows (with serviceName/seats/interval). Same strategy as BusinessWeekScheduler.
  const handleSaveHours = async () => {
    setHoursSaving(true);
    const delResults = await Promise.all(hoursLoadedIds.map((id) => openingHoursSvc.delHour(id)));
    const adds = [];
    hours.forEach((h, dayIdx) => {
      if (!h.open) return;
      h.services.forEach((s) => {
        if (s.from && s.to) {
          adds.push({
            type: 1, area: 0, dayInWeek: dayIdx + 1,
            startTime: `${s.from}:00`, endTime: `${s.to}:00`,
            serviceName: s.name || 'סרביס',
            seats: Number(s.seats) || 0,
            intervalMinutes: Number(s.interval) || 0,
          });
        }
      });
    });
    const addResults = await Promise.all(adds.map((a) => openingHoursSvc.addHour(a)));
    const failed = delResults.filter((r) => !r || !r.ok).length + addResults.filter((r) => !r || !r.ok).length;
    const { days, ids } = buildHoursFromRows(await openingHoursSvc.listHours());
    setHours(days); setHoursLoadedIds(ids);
    setHoursSaving(false);
    setToast(failed ? `נשמר חלקית — ${failed} פעולות נכשלו` : 'שעות הפעילות נשמרו');
  };

  // Transient demo toast (per-section "שמירה").
  const [toast, setToast] = useState('');
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(t);
  }, [toast]);


  /* ================================================================ *
   *  CATEGORIES
   * ================================================================ */
  const [newCat, setNewCat] = useState('');

  const addCategory = async (e) => {
    e.preventDefault();
    const v = newCat.trim();
    setNewCat('');
    if (!v || categories.some((c) => c.name === v)) return;
    const res = await categoriesSvc.addCategory(v);
    if (res.ok) { setCategories((prev) => [...prev, { id: res.id, name: v }]); }
    else { setToast('הוספת הקטגוריה נכשלה'); }
  };
  const removeCategory = async (id) => {
    setCategories((prev) => prev.filter((c) => c.id !== id));
    const res = await categoriesSvc.delCategory(id);
    if (!res.ok) { setToast('המחיקה נכשלה'); setCategories(await categoriesSvc.listCategories()); }
  };

  const renderCategories = () => (
    <section className="bc-panel" aria-label="קטגוריות">
      <div className="bc-panel-head">
        <div>
          <h2 className="bc-panel-title">קטגוריות העסק</h2>
          <p className="bc-panel-sub">
            תגיות מטבח וסגנון — מזינות את סינון הקטגוריות בעמוד הציבורי ואת קיבוץ התפריט.
          </p>
        </div>
      </div>

      {catLoading ? (
        <div className="bc-empty"><p>טוען קטגוריות…</p></div>
      ) : categories.length === 0 ? (
        <div className="bc-empty">
          <div className="bc-empty-emoji" aria-hidden="true"><Icon name="tag" size={30} /></div>
          <p>עדיין אין קטגוריות</p>
          <p className="bc-empty-sub">הוסיפו קטגוריה ראשונה כדי שלקוחות יוכלו לסנן לפיה.</p>
        </div>
      ) : (
        <div className="bc-chips">
          {categories.map((c) => (
            <span className="bc-chip" key={c.id}>
              <span className="bc-chip-txt">{c.name}</span>
              <button
                type="button"
                className="bc-chip-x"
                onClick={() => removeCategory(c.id)}
                aria-label={`הסרת ${c.name}`}
                title="הסרה"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <form className="bc-add-row" onSubmit={addCategory}>
        <input
          className="bc-input"
          type="text"
          value={newCat}
          onChange={(e) => setNewCat(e.target.value)}
          placeholder="קטגוריה חדשה (למשל: כשר, טבעוני, בשרים)"
          aria-label="קטגוריה חדשה"
        />
        <button type="submit" className="bc-btn-add" disabled={!newCat.trim()}>
          ＋ הוספה
        </button>
      </form>
      {/* No save bar — categories persist immediately on add/remove. */}
    </section>
  );

  /* ================================================================ *
   *  MENU
   * ================================================================ */
  const [collapsed, setCollapsed] = useState({}); // { [catIdx]: true }
  const [newMenuCat, setNewMenuCat] = useState('');
  // Per-category inline "add dish" drafts.
  const [dishDraft, setDishDraft] = useState({}); // { [catIdx]: {name,desc,price} }

  const toggleCollapse = (idx) =>
    setCollapsed((prev) => ({ ...prev, [idx]: !prev[idx] }));

  const addMenuCat = (e) => {
    e.preventDefault();
    const v = newMenuCat.trim();
    if (!v) return;
    setMenu((prev) => [...prev, { cat: v, dishes: [] }]);
    setNewMenuCat('');
  };

  const removeMenuCat = (idx) => {
    // Real delete: soft-delete every dish in the category (each has a DB id), then drop
    // the local group. A brand-new empty category has no DB rows yet — just drop it.
    const cat = menu[idx];
    setMenu((prev) => prev.filter((_, i) => i !== idx));
    if (cat && cat.dishes.length) {
      Promise.all(cat.dishes.filter((d) => d.id).map((d) => menuSvc.delItem(d.id)))
        .then((results) => { if (results.some((r) => !r || !r.ok)) { setToast('חלק מהמחיקות נכשלו'); refreshMenu(); } });
    }
    // collapsed/dishDraft are keyed by category index — shift keys above the
    // removed index down by one so per-category UI state stays on the right category.
    const reindex = (obj) => {
      const next = {};
      Object.keys(obj).forEach((k) => {
        const n = Number(k);
        if (n < idx) next[n] = obj[k];
        else if (n > idx) next[n - 1] = obj[k];
      });
      return next;
    };
    setCollapsed(reindex);
    setDishDraft(reindex);
  };

  const draftFor = (idx) => dishDraft[idx] || { name: '', desc: '', price: '' };
  const setDraft = (idx, patch) =>
    setDishDraft((prev) => ({ ...prev, [idx]: { ...draftFor(idx), ...patch } }));

  const addDish = async (idx) => {
    const d = draftFor(idx);
    if (!d.name.trim()) return;
    const cat = menu[idx].cat;
    const sortOrder = menu[idx].dishes.length;
    const dish = { name: d.name.trim(), desc: d.desc.trim(), price: Number(d.price) || 0 };
    setDishDraft((prev) => ({ ...prev, [idx]: { name: '', desc: '', price: '' } }));
    // Persist first so the dish carries its real DB id (needed for later edit/delete).
    const res = await menuSvc.addItem({ category: cat, name: dish.name, description: dish.desc, price: dish.price, sortOrder });
    if (res.ok) {
      // Append by CATEGORY IDENTITY (idx may be stale after the await).
      setMenu((prev) => prev.map((c) => (c.cat === cat ? { ...c, dishes: [...c.dishes, { ...dish, id: res.id }] } : c)));
    } else {
      setToast('לא ניתן להוסיף מנה — נסו שוב');
    }
  };

  const removeDish = async (catIdx, dishIdx) => {
    const dish = menu[catIdx].dishes[dishIdx];
    if (!dish) return;
    // Remove by dish IDENTITY (robust to index shifts).
    setMenu((prev) => prev.map((c) => ({ ...c, dishes: c.dishes.filter((x) => x !== dish) })));
    if (dish.id) {
      const res = await menuSvc.delItem(dish.id);
      if (!res.ok) { setToast('המחיקה נכשלה'); refreshMenu(); }
    }
  };

  // Inline price edit: update locally on change; PERSIST on blur (avoids an API call per keystroke).
  const editDishPrice = (catIdx, dishIdx, value) => {
    const price = value === '' ? '' : Number(value);
    setMenu((prev) =>
      prev.map((c, i) =>
        i === catIdx
          ? { ...c, dishes: c.dishes.map((d, j) => (j === dishIdx ? { ...d, price: Number.isNaN(price) ? d.price : price } : d)) }
          : c
      )
    );
  };

  const saveDishPrice = async (catIdx, dishIdx) => {
    const dish = menu[catIdx].dishes[dishIdx];
    if (!dish || !dish.id) return;
    const res = await menuSvc.editItem({ id: dish.id, name: dish.name, price: Number(dish.price) || 0 });
    if (!res.ok) { setToast('שמירת המחיר נכשלה'); refreshMenu(); }
  };

  const renderMenu = () => (
    <section className="bc-panel" aria-label="תפריט">
      <div className="bc-panel-head">
        <div>
          <h2 className="bc-panel-title">עורך תפריט</h2>
          <p className="bc-panel-sub">
            קבצו מנות לקטגוריות (ראשונות, עיקריות, קינוחים…). כל שינוי כאן ישתקף בעמוד הציבורי.
          </p>
        </div>
      </div>

      {menuLoading ? (
        <div className="bc-empty"><p>טוען תפריט…</p></div>
      ) : menu.length === 0 ? (
        <div className="bc-empty">
          <div className="bc-empty-emoji" aria-hidden="true"><Icon name="list" size={30} /></div>
          <p>התפריט עדיין ריק</p>
          <p className="bc-empty-sub">הוסיפו קטגוריית תפריט ראשונה כדי להתחיל.</p>
        </div>
      ) : (
        <div className="bc-menu">
          {menu.map((c, idx) => {
            const isCollapsed = !!collapsed[idx];
            const draft = draftFor(idx);
            return (
              <div className="bc-menu-cat" key={`${c.cat}-${idx}`}>
                <div className="bc-menu-cat-head">
                  <button
                    type="button"
                    className="bc-menu-cat-toggle"
                    onClick={() => toggleCollapse(idx)}
                    aria-expanded={!isCollapsed}
                  >
                    <span className={`bc-caret ${isCollapsed ? 'is-collapsed' : ''}`} aria-hidden="true">▾</span>
                    <span className="bc-menu-cat-name">{c.cat}</span>
                    <span className="bc-menu-cat-count">{c.dishes.length} מנות</span>
                  </button>
                  <button
                    type="button"
                    className="bc-icon-danger"
                    onClick={() => removeMenuCat(idx)}
                    aria-label={`הסרת קטגוריה ${c.cat}`}
                    title="הסרת קטגוריה"
                  >
                    <Icon name="trash" size={16} />
                  </button>
                </div>

                {!isCollapsed && (
                  <div className="bc-menu-cat-body">
                    {c.dishes.length === 0 && (
                      <p className="bc-menu-empty">אין מנות בקטגוריה זו עדיין.</p>
                    )}
                    {c.dishes.map((d, j) => (
                      <div className="bc-dish" key={d.id || `${d.name}-${j}`}>
                        <div className="bc-dish-info">
                          <span className="bc-dish-name">{d.name}</span>
                          {d.desc && <span className="bc-dish-desc">{d.desc}</span>}
                        </div>
                        <div className="bc-dish-price">
                          <span className="bc-shekel" aria-hidden="true">₪</span>
                          <input
                            className="bc-price-input"
                            type="number"
                            min="0"
                            inputMode="numeric"
                            value={d.price}
                            onChange={(e) => editDishPrice(idx, j, e.target.value)}
                            onBlur={() => saveDishPrice(idx, j)}
                            aria-label={`מחיר ${d.name}`}
                          />
                        </div>
                        <button
                          type="button"
                          className="bc-icon-danger"
                          onClick={() => removeDish(idx, j)}
                          aria-label={`הסרת ${d.name}`}
                          title="הסרת מנה"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    {/* Inline add-dish form */}
                    <div className="bc-dish-add">
                      <input
                        className="bc-input"
                        type="text"
                        value={draft.name}
                        onChange={(e) => setDraft(idx, { name: e.target.value })}
                        placeholder="שם המנה"
                        aria-label="שם מנה חדשה"
                      />
                      <input
                        className="bc-input"
                        type="text"
                        value={draft.desc}
                        onChange={(e) => setDraft(idx, { desc: e.target.value })}
                        placeholder="תיאור קצר"
                        aria-label="תיאור מנה"
                      />
                      <input
                        className="bc-input bc-input-price"
                        type="number"
                        min="0"
                        inputMode="numeric"
                        value={draft.price}
                        onChange={(e) => setDraft(idx, { price: e.target.value })}
                        placeholder="₪"
                        aria-label="מחיר מנה"
                      />
                      <button
                        type="button"
                        className="bc-btn-add"
                        onClick={() => addDish(idx)}
                        disabled={!draft.name.trim()}
                      >
                        ＋ מנה
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <form className="bc-add-row" onSubmit={addMenuCat}>
        <input
          className="bc-input"
          type="text"
          value={newMenuCat}
          onChange={(e) => setNewMenuCat(e.target.value)}
          placeholder="קטגוריית תפריט חדשה (למשל: מרקים, שתייה)"
          aria-label="קטגוריית תפריט חדשה"
        />
        <button type="submit" className="bc-btn-add" disabled={!newMenuCat.trim()}>
          ＋ קטגוריה
        </button>
      </form>

      {/* No save bar — menu changes persist to the server immediately. */}
    </section>
  );

  /* ================================================================ *
   *  GALLERY
   * ================================================================ */
  // Real upload: read the chosen file as a base64 data-URI and send to galleryUpload
  // (the server validates, re-encodes to JPEG, and stores it). Then re-sync from the server.
  const onPickImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { setToast('רק תמונות JPG / PNG / WEBP'); return; }
    if (file.size > 5 * 1024 * 1024) { setToast('התמונה גדולה מדי (עד 5MB)'); return; }
    setUploading(true);
    const dataUri = await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(file);
    });
    const res = dataUri ? await gallerySvc.uploadImage(dataUri) : { ok: false };
    setUploading(false);
    if (res.ok) { await refreshGallery(); setToast('התמונה הועלתה'); }
    else { setToast('העלאת התמונה נכשלה — נסו תמונה אחרת'); }
  };

  const removeTile = async (id) => {
    setGallery((prev) => prev.filter((t) => t.id !== id));
    const res = await gallerySvc.deleteImage(id);
    if (!res.ok) { setToast('המחיקה נכשלה'); refreshGallery(); }
  };

  const setCover = async (id) => {
    setGallery((prev) => prev.map((t) => ({ ...t, isCover: t.id === id })));
    const res = await gallerySvc.setCoverImage(id);
    if (!res.ok) { setToast('הגדרת תמונת השער נכשלה'); refreshGallery(); }
  };

  // Gradient rotation for tiles (all token-based, no external URLs).
  const tileGradients = [
    'linear-gradient(135deg, var(--brand-soft), var(--bg-subtle))',
    'linear-gradient(135deg, var(--brand-tint), var(--brand-soft))',
    'linear-gradient(135deg, var(--bg-subtle), var(--brand-tint))',
  ];

  const renderGallery = () => (
    <section className="bc-panel" aria-label="גלריה">
      <div className="bc-panel-head">
        <div>
          <h2 className="bc-panel-title">גלריית תמונות</h2>
          <p className="bc-panel-sub">
            תמונות שהלקוחות יראו בעמוד העסק. התמונות נשמרות לשרת. סמנו תמונת שער אחת.
          </p>
        </div>
      </div>

      {galleryLoading ? (
        <p className="bc-gallery-hint">טוען גלריה…</p>
      ) : (
      <div className="bc-gallery">
        {gallery.map((t, i) => (
          <div
            className={`bc-tile ${t.isCover ? 'is-cover' : ''}`}
            key={t.id}
            style={{ background: tileGradients[i % tileGradients.length] }}
          >
            {t.isCover && <span className="bc-tile-cover-tag">תמונת שער</span>}
            {t.filePath
              ? <img className="bc-tile-img" src={t.filePath} alt={t.caption || 'תמונת גלריה'} loading="lazy"
                     style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
              : <span className="bc-tile-emoji" aria-hidden="true"><Icon name="image" size={26} /></span>}
            <div className="bc-tile-actions">
              {!t.isCover && (
                <button type="button" className="bc-tile-btn" onClick={() => setCover(t.id)} title="הגדר כתמונת שער">
                  <Icon name="check" size={14} style={{ verticalAlign: 'middle' }} /> שער
                </button>
              )}
              <button type="button" className="bc-tile-x" onClick={() => removeTile(t.id)} aria-label="הסרת תמונה" title="הסרה">×</button>
            </div>
          </div>
        ))}

        {/* Real upload → galleryUpload (server validates + re-encodes + stores). */}
        <label className={`bc-tile bc-tile-add ${uploading ? 'is-uploading' : ''}`} style={{ cursor: uploading ? 'wait' : 'pointer' }}>
          <span className="bc-tile-add-plus" aria-hidden="true">{uploading ? '…' : '＋'}</span>
          <span className="bc-tile-add-lbl">{uploading ? 'מעלה…' : 'העלאת תמונה'}</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickImage} disabled={uploading} style={{ display: 'none' }} />
        </label>
      </div>
      )}

      {!galleryLoading && gallery.length === 0 && (
        <p className="bc-gallery-hint">הגלריה ריקה — העלו תמונה כדי להתחיל.</p>
      )}
    </section>
  );

  /* ================================================================ *
   *  HOURS + SERVICES
   * ================================================================ */
  const toggleDayOpen = (dayIdx) =>
    setHours((prev) =>
      prev.map((h, i) => {
        if (i !== dayIdx) return h;
        const open = !h.open;
        // Opening a day with no services seeds one sensible default sitting.
        const services =
          open && h.services.length === 0
            ? [{ name: 'סרביס', from: '12:00', to: '22:00', seats: 60, interval: 90 }]
            : h.services;
        return { ...h, open, services };
      })
    );

  const addService = (dayIdx) =>
    setHours((prev) =>
      prev.map((h, i) =>
        i === dayIdx
          ? {
              ...h,
              services: [
                ...h.services,
                { name: 'סרביס חדש', from: '18:00', to: '23:00', seats: 60, interval: 90 },
              ],
            }
          : h
      )
    );

  const removeService = (dayIdx, svcIdx) =>
    setHours((prev) =>
      prev.map((h, i) =>
        i === dayIdx
          ? { ...h, services: h.services.filter((_, j) => j !== svcIdx) }
          : h
      )
    );

  const editService = (dayIdx, svcIdx, patch) =>
    setHours((prev) =>
      prev.map((h, i) =>
        i === dayIdx
          ? {
              ...h,
              services: h.services.map((s, j) => (j === svcIdx ? { ...s, ...patch } : s)),
            }
          : h
      )
    );

  const renderHours = () => (
    <section className="bc-panel" aria-label="שעות פעילות וסרביסים">
      <div className="bc-panel-head">
        <div>
          <h2 className="bc-panel-title">שעות פעילות + סרביסים</h2>
          <p className="bc-panel-sub">
            לכל יום — פתוח/סגור, ובתוך היום ניתן להגדיר סרביסים (סבבים) עם חלון שעות ומקומות ישיבה.
          </p>
        </div>
      </div>

      <div className="bc-days">
        {hours.map((h, dayIdx) => (
          <div className={`bc-day ${h.open ? '' : 'is-closed'}`} key={h.day}>
            <div className="bc-day-head">
              <span className="bc-day-name">{h.day}</span>
              <label className="bc-toggle">
                <input
                  type="checkbox"
                  checked={h.open}
                  onChange={() => toggleDayOpen(dayIdx)}
                  aria-label={`פתוח ביום ${h.day}`}
                />
                <span className="bc-toggle-track" aria-hidden="true">
                  <span className="bc-toggle-thumb" />
                </span>
                <span className="bc-toggle-lbl">{h.open ? 'פתוח' : 'סגור'}</span>
              </label>
            </div>

            {h.open && (
              <div className="bc-services">
                {h.services.length === 0 && (
                  <p className="bc-menu-empty">אין סרביסים — הוסיפו סרביס ליום זה.</p>
                )}
                {h.services.map((s, svcIdx) => (
                  <div className="bc-service" key={svcIdx}>
                    <input
                      className="bc-input bc-service-name"
                      type="text"
                      value={s.name}
                      onChange={(e) => editService(dayIdx, svcIdx, { name: e.target.value })}
                      placeholder="שם הסרביס"
                      aria-label="שם הסרביס"
                    />
                    <div className="bc-service-times">
                      <label className="bc-time-field">
                        <span>מ־</span>
                        <input
                          className="bc-input bc-input-time"
                          type="time"
                          value={s.from}
                          onChange={(e) => editService(dayIdx, svcIdx, { from: e.target.value })}
                          aria-label="שעת התחלה"
                        />
                      </label>
                      <label className="bc-time-field">
                        <span>עד</span>
                        <input
                          className="bc-input bc-input-time"
                          type="time"
                          value={s.to}
                          onChange={(e) => editService(dayIdx, svcIdx, { to: e.target.value })}
                          aria-label="שעת סיום"
                        />
                      </label>
                    </div>
                    <div className="bc-service-opts">
                      <label className="bc-opt-field">
                        <span>מקומות</span>
                        <input
                          className="bc-input bc-input-num"
                          type="number"
                          min="0"
                          inputMode="numeric"
                          value={s.seats}
                          onChange={(e) =>
                            editService(dayIdx, svcIdx, { seats: Number(e.target.value) || 0 })
                          }
                          aria-label="מספר מקומות"
                        />
                      </label>
                      <label className="bc-opt-field">
                        <span>מרווח (דק׳)</span>
                        <input
                          className="bc-input bc-input-num"
                          type="number"
                          min="0"
                          step="15"
                          inputMode="numeric"
                          value={s.interval}
                          onChange={(e) =>
                            editService(dayIdx, svcIdx, { interval: Number(e.target.value) || 0 })
                          }
                          aria-label="מרווח בין ישיבות בדקות"
                        />
                      </label>
                      <button
                        type="button"
                        className="bc-icon-danger bc-service-remove"
                        onClick={() => removeService(dayIdx, svcIdx)}
                        aria-label={`הסרת סרביס ${s.name}`}
                        title="הסרת סרביס"
                      >
                        <Icon name="trash" size={16} />
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="bc-btn-add bc-service-add"
                  onClick={() => addService(dayIdx)}
                >
                  ＋ הוספת סרביס
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="bc-save-bar">
        <button type="button" className="bc-btn-primary" onClick={handleSaveHours} disabled={hoursSaving}>
          {hoursSaving ? 'שומר…' : 'שמירת שעות'}
        </button>
      </div>
    </section>
  );

  /* ================================================================ */

  const activeTab = useMemo(() => TABS.find((t) => t.id === tab), [tab]);

  return (
    <div className="bc-page">
      <div className="bc-demo-note">
        <Icon name="info" size={15} style={{ verticalAlign: 'middle' }} /> כל התוכן כאן — קטגוריות, תפריט, גלריה ושעות — נשמר לשרת (אמיתי).
      </div>

      {/* ---------------- Header ---------------- */}
      <header className="bc-header">
        <div className="bc-header-main">
          <h1 className="bc-title">ניהול תוכן העסק</h1>
          <p className="bc-subtitle">
            כאן מנהלים את מה שהלקוחות רואים בעמוד העסק — קטגוריות, תפריט, גלריה ושעות פעילות.
          </p>
        </div>
      </header>

      {/* ---------------- Segmented control ---------------- */}
      <div className="bc-tabs" role="tablist" aria-label="בחירת מקטע">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`bc-tab ${tab === t.id ? 'is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <span className="bc-tab-emoji" aria-hidden="true"><Icon name={t.icon} size={16} /></span>
            <span className="bc-tab-lbl">{t.label}</span>
          </button>
        ))}
      </div>

      {/* ---------------- Active section ---------------- */}
      <div className="bc-body" aria-label={activeTab?.label}>
        {tab === 'categories' && renderCategories()}
        {tab === 'menu' && renderMenu()}
        {tab === 'gallery' && renderGallery()}
        {tab === 'hours' && renderHours()}
      </div>

      {toast && (
        <div className="bc-toast" role="status">
          <span className="bc-toast-ico" aria-hidden="true">✓</span>
          {toast}
        </div>
      )}
    </div>
  );
};

export default BusinessContent;
